import React, { useEffect, useState, useCallback, useMemo } from 'react';
import * as Yup from 'yup';
import validarCpf from '../../utils/validarCpf';
import api from '../../services/api';

import Button from '../Button';
import { useToast } from '../../hooks/toast';
import getResponseErrors from '../../utils/getResponseErrors';
import { useAuth } from '../../hooks/auth';
import { validarCNPJ } from '../../utils/funcoes';

interface IDadosRps {
  CprCodigo: number;
  CprDataPag: string;
  CprHistorico: string;
  NfeCliCNPJ: string | null;
  NfeCliEmail: string | null;
  NfeCliNome: string | null;
  NfeCodigo: string | null;
  NfeCodigoVerificacao: string | null;
  NfeCprCodigo: string | null;
  NfeDataEmissao: string | null;
  NfeLinkNFSe: string | null;
  NfeNumero: string | null;
  NfeNumeroNFSe: string | null;
  NfeSerie: string | null;
  NfeSituacao: string | null;
  NfeValorNota: string | null;
  PacCodigo: number;
  Respcnpj: string | null;
  Respie: string | null;
  Respnome: string | null;
  bairro: string | null;
  cep: string | null;
  cidade: string | null;
  cidadeCodigo: string | null;
  cpf: string | null;
  codigo: number;
  complemento: string | null;
  descricao: string | null;
  email: string | null;
  endereco: string | null;
  fone: string | null;
  ie: string | null;
  nome: string | null;
  numero: string | null;
  uf: 'RJ';
  vlr: 70;
  SerAliquota: string | null;
  SerCNAE: string | null;
  SerCodigo: number | null;
  SerCodigoNBS: string | null;
  SerCodigoServico: string | null;
  SerCodigoTributacao: string | null;
  SerDescricao: string | null;
  SerIbsClassTrib: string | null;
  SerIbsSituTrib: string | null;
  SerIndicOp: string | null;
  SerPerCodigo: number | null;
  SerTipoTributacao: number | null;
  SerUsuCodigo: string | null;
}

interface ITagErro {
  [key: string]: {
    valor: string;
    erro: boolean;
  };
}

interface IValidationError {
  value: any;
  path?: string;
  errors: string[];
}
interface IValidationErrors {
  value?: any;
  path?: string;
  errors?: string[];
  inner?: IValidationError[];
}

const schemaNaoOptanteSimples = Yup.object().shape({
  SerTribFederal: Yup.string().required(
    'Aliquota de Tributo Federal obrigatório.',
  ),
  SerTribEstadual: Yup.string().required(
    'Aliquota de Tributo Estadual obrigatório.',
  ),
  SerTribMunicipal: Yup.string().required(
    'Aliquota de Tributo Municipal obrigatório.',
  ),
});

const schemaSimplesNacional = Yup.object().shape({
  nome: Yup.string().required('Nome obrigatório'),
  cpf: Yup.string().required('CPF obrigatório'),
  ie: Yup.string(),
  endereco: Yup.string().required('Endereço obrigatório'),
  numero: Yup.string().required('Número obrigatório'),
  bairro: Yup.string().required('Bairro obrigatório'),
  cep: Yup.string().required('CEP obrigatório'),
  cidadeCodigo: Yup.string().required('Código da Cidade obrigatório'),
  cidade: Yup.string().required('Cidade obrigatória'),
  uf: Yup.string().required('Estado obrigatório'),
  fone: Yup.string().required('Telefone obrigatório'),
  email: Yup.string().email('Email inválido').required('E-mail obrigatório'),
  descricao: Yup.string().required('Serviço obrigatório'),
  vlr: Yup.string().required('Valor obrigatório'),
  SerAliquota: Yup.string().required('Alíquota obrigatório'),
  SerCNAE: Yup.string().required('CNAE obrigatório'),
  SerCodigoNBS: Yup.string().required('Código NBS obrigatório'),
  SerCodigoServico: Yup.string().required('Código Serviço obrigatório'),
});

type DadosNotaProps = {
  codigo: string;
  handleFecharModal(): void;
};

const DadosNota: React.FC<DadosNotaProps> = ({ codigo, handleFecharModal }) => {
  const { clinica } = useAuth();
  const [dados, setDados] = useState<ITagErro>({});
  const [erroDados, setErroDados] = useState(false);
  const [nomeBotao, setNomeBotao] = useState('Carregando...');

  const { addToast } = useToast();

  const simplesNacional =
    clinica.CliSimplesNacional || clinica.CliNomeAbreviado === 'Unidade Angra';

  const schema = useMemo(() => {
    return simplesNacional
      ? schemaSimplesNacional
      : schemaNaoOptanteSimples.concat(schemaSimplesNacional);
  }, [simplesNacional]);

  const EnviarRps = useCallback(async () => {
    setNomeBotao('Carregando...');
    try {
      await api.post(`/emissaonfse/registraNFSE/${codigo}`);
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível carregar dados da Nota Fiscal',
        type: 'error',
        description: mensagemErro,
      });
    }
    setNomeBotao('Gerar Nota Fiscal');
    handleFecharModal();
  }, [handleFecharModal, codigo, addToast]);

  const ForcarEmissao = useCallback(async () => {
    setNomeBotao('Carregando...');
    console.log(dados);
    try {
      if (
        !dados.cpf.valor ||
        dados.cpf.valor === '' ||
        (!validarCpf(dados.cpf.valor) && !validarCNPJ(dados.cpf.valor))
      ) {
        addToast({
          title: 'Cpf/CNPJ Inválido!',
          type: 'error',
          description: 'Verifique o cpf/cnpj antes do envio.',
        });
        return;
      }
      if (!dados.SerAliquota.valor || dados.SerAliquota.valor === '') {
        addToast({
          title: 'Alíquota Inválida!',
          type: 'error',
          description: 'Verifique a alíquota antes do envio.',
        });
        return;
      }
      if (!dados.SerCNAE.valor || dados.SerCNAE.valor === '') {
        addToast({
          title: 'CNAE Inválido!',
          type: 'error',
          description: 'Verifique o CNAE antes do envio.',
        });
        return;
      }
      if (!dados.SerCodigoNBS.valor || dados.SerCodigoNBS.valor === '') {
        addToast({
          title: 'Código NBS Inválido!',
          type: 'error',
          description: 'Verifique o Código NBS antes do envio.',
        });
        return;
      }
      if (
        !dados.SerCodigoServico.valor ||
        dados.SerCodigoServico.valor === ''
      ) {
        addToast({
          title: 'Código Serviço Inválido!',
          type: 'error',
          description: 'Verifique o Código Serviço antes do envio.',
        });
        return;
      }

      if (!simplesNacional) {
        if (!dados.SerTribFederal.valor || dados.SerTribFederal.valor === '') {
          addToast({
            title: 'Valor do Tributário Federal Inválido!',
            type: 'error',
            description:
              'Verifique o Valor do Tributário Federal antes do envio.',
          });
          return;
        }
        if (
          !dados.SerTribEstadual.valor ||
          dados.SerTribEstadual.valor === ''
        ) {
          addToast({
            title: 'Valor do Tributário Estadual Inválido!',
            type: 'error',
            description:
              'Verifique o Valor do Tributário Estadual antes do envio.',
          });
          return;
        }
        if (
          !dados.SerTribMunicipal.valor ||
          dados.SerTribMunicipal.valor === ''
        ) {
          addToast({
            title: 'Valor do Tributário Municipal Inválido!',
            type: 'error',
            description:
              'Verifique o Valor do Tributário Municipal antes do envio.',
          });
          return;
        }
      }

      await api.post(`/emissaonfse/registraNFSEAuto/${codigo}?auto=1`);
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível carregar dados da Nota Fiscal',
        type: 'error',
        description: mensagemErro,
      });
    }
    setNomeBotao('Gerar Nota Fiscal');
    handleFecharModal();
  }, [handleFecharModal, codigo, addToast, dados, simplesNacional]);

  useEffect(() => {
    async function load(): Promise<void> {
      try {
        const nf = await api.get(`/rps/DadosTomador?CprCodigo=${codigo}`);

        const retorno: IDadosRps = {
          ...nf.data[0],
          descricao: nf.data[0].SerDescricao,
        };
        let erro: IValidationErrors = {};
        let errocpf = false;
        try {
          await schema.validate(retorno, {
            abortEarly: false,
          });

          console.log(retorno.cpf);
          if (retorno.cpf && validarCpf(retorno.cpf)) {
            // this.CmdConfirmar.removeAttribute('disabled');
            console.log('dentro cpf  validado');
          } else {
            errocpf = true;
            // this.TxtCnpj.className = 'form-control form-control-sm bg-danger';
            // this.CmdConfirmar.setAttribute('disabled', 'disabled');
            console.log('dentro cpf NÂO validado');
          }
        } catch (error: any) {
          // console.dir(error);
          erro = error;
          // error.inner.forEach(erro => {
          // this.campo = document.getElementsByName(erro.path);
          //   console.dir(erro);
          //  this.campo[0].className =
          //   ('form-control form-control-sm bg-danger');
          // });
          //  this.CmdConfirmar.setAttribute('disabled', 'disabled');
          // alert(error.errors);
        }

        const retornoTagErro: ITagErro = {};
        Object.keys(schema.fields).forEach((it: string) => {
          Object.entries(retorno).forEach(([key, value]) => {
            if (key === it)
              retornoTagErro[it] = {
                valor: value,
                erro: false,
              };
          });
        });
        if (errocpf) {
          retornoTagErro.cpf.erro = true;
        }

        setErroDados(!!erro.inner);
        if (erro.inner) {
          erro.inner.forEach(err => {
            if (err.path)
              try {
                retornoTagErro[err.path].erro = true;
              } catch (error2) {
                console.log(err.path);
              }
          });
        }
        setDados(retornoTagErro);
      } catch (error) {
        console.log(error);
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível validar dados',
          type: 'error',
          description: mensagemErro,
        });
      }
      setNomeBotao('Gerar Nota Fiscal');
    }
    load();
  }, [codigo, addToast, schema]);

  const nomeCampo = useCallback(campo => {
    if (campo === 'ie') return 'RG';
    if (campo === 'cpf') return 'CPF';
    if (campo === 'vlr') return 'Valor';
    if (campo === 'fone') return 'telefone';
    if (campo === 'uf') return 'estado';
    if (campo === 'SerAliquota') return 'Alíquota';
    if (campo === 'SerCNAE') return 'CNAE';
    if (campo === 'SerCodigoNBS') return 'Código NBS';
    if (campo === 'SerCodigoServico') return 'Código Serviço';
    if (campo === 'SerTribFederal') return 'Trib. Federal';
    if (campo === 'SerTribEstadual') return 'Trib. Estadual';
    if (campo === 'SerTribMunicipal') return 'Trib. Municipal';
    return campo;
  }, []);
  console.log(schema.fields);

  return (
    <>
      <ul
        style={{
          display: 'grid',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '10px',
          margin: '0',
          gridTemplateColumns: '1fr 1fr',
        }}
      >
        {Object.keys(schema.fields).map(it =>
          dados[it] ? (
            <li
              style={{
                listStyle: 'none',
                padding: '0 7px',
                display: 'block',
              }}
              key={it}
            >
              <span
                style={{
                  padding: '0 5px',
                  textTransform: 'capitalize',
                  fontWeight: 'bold',
                }}
              >
                {nomeCampo(it)}
              </span>
              <div
                style={{
                  border: '1px solid #eee',
                  borderRadius: '4px',
                  padding: '3px',
                  minHeight: '32px',
                  backgroundColor: dados[it].erro ? 'red' : '#eee',
                }}
              >
                {dados[it].valor}
              </div>
            </li>
          ) : (
            ''
          ),
        )}
      </ul>
      <div className="float-right">
        <Button
          disabled={!erroDados}
          type="button"
          className="btn btn-sm btn-primary md-2 mb-2 mr-3"
          onClick={() => ForcarEmissao()}
        >
          Forçar Emissão
        </Button>

        <Button
          disabled={erroDados}
          type="button"
          className="btn btn-sm btn-primary md-2 mb-2 mr-3"
          onClick={() => EnviarRps()}
          name={nomeBotao}
        >
          {nomeBotao}
        </Button>
      </div>
    </>
  );
};

export default DadosNota;
